import { apiCall } from '@/lib/api-client';

/** Mirrors backend/schemas.py::DashboardEntry / DashboardResponse. */
export type DashboardAgent = 'predicate' | 'strategy' | 'protocol' | 'report' | 'dossier';

/** The three paginated list sections (URL segments on /api/dashboard). */
export type DashboardSection = 'needs-attention' | 'running-now' | 'recent-activity';

export interface DashboardEntryDto {
  agent: DashboardAgent;
  product_id: number;
  product_name: string | null;
  country: string | null;
  run_id: number;
  at: string | null;
  /** "failed" | "out_of_date" — drives label and tone in Needs Attention. */
  reason: string;
  /** Which finder a 'predicate' entry is — the drug / biologic finders share the
   *  predicate run table. null / absent for every other agent. */
  finder_kind?: 'predicate' | 'generic' | 'reference' | null;
}

/** Single bundle: KPIs + the first page (5) of each list section + cursors.
 *  One request per dashboard load. "Load more" pages come from
 *  getDashboardSection. */
export interface DashboardDto {
  total_products: number;
  in_flight_runs: number;
  products_with_dossier: number;
  markets_covered: string[];
  by_type: Record<string, number>;
  market_coverage: Record<string, number>;
  needs_attention_count: number;
  recent_activity_count: number;
  running_now: DashboardEntryDto[];
  running_now_cursor: string | null;
  needs_attention: DashboardEntryDto[];
  needs_attention_cursor: string | null;
  recent_activity: DashboardEntryDto[];
  recent_activity_cursor: string | null;
}

/** One keyset page of a dashboard list section. */
export interface DashboardSectionPageDto {
  items: DashboardEntryDto[];
  next_cursor: string | null;
}

/** Server-side KPI rollup — everything the dashboard's tiles and charts
 *  render, without fetching every product summary. */
export async function getDashboard(): Promise<DashboardDto> {
  return apiCall<DashboardDto>('/api/dashboard', { method: 'GET' });
}

/** One page of a dashboard list section. `cursor` null = first page. */
export async function getDashboardSection(
  section: DashboardSection,
  cursor: string | null,
  limit = 5,
): Promise<DashboardSectionPageDto> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  return apiCall<DashboardSectionPageDto>(
    `/api/dashboard/${section}?${params.toString()}`,
    { method: 'GET' },
  );
}
